import { useMemo } from "react";
import { scaleTime, type ScaleLinear } from "d3";

type AxisBottomProps = {
  xScale: ScaleLinear<number, number>;
  pixelsPerTick: number;
};

// tick length
const TICK_LENGTH = 4;

const formatMonth = (date: Date) => {
  return date.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
};

export const AxisBottom = ({ xScale, pixelsPerTick }: AxisBottomProps) => {
  const axis = useMemo(() => {
    const range = xScale.range();
    const domain = xScale.domain();
    const rangeStart = range[0];
    const rangeEnd = range[1];
    const domainStart = domain[0];
    const domainEnd = domain[1];
    if (
      rangeStart === undefined ||
      rangeEnd === undefined ||
      domainStart === undefined ||
      domainEnd === undefined
    ) {
      // A scale missing either end of either interval has no axis to draw.
      return null;
    }

    const width = rangeEnd - rangeStart;

    // Use a time scale to get nice month-based ticks
    const timeScale = scaleTime()
      .domain([new Date(domainStart), new Date(domainEnd)])
      .range([rangeStart, rangeEnd]);

    const numberOfTicksTarget = Math.floor(width / pixelsPerTick);

    return {
      width,
      ticks: timeScale.ticks(numberOfTicksTarget).map((date) => ({
        value: date.getTime(),
        xOffset: xScale(date.getTime()),
        label: formatMonth(date),
      })),
    };
  }, [xScale, pixelsPerTick]);

  if (axis === null) {
    return null;
  }

  return (
    <>
      <line
        y1={0}
        y2={0}
        x1={0}
        x2={axis.width}
        stroke="#D2D7D3"
        strokeWidth={0.5}
        shapeRendering={"crispEdges"}
      />
      {/* Ticks and labels */}
      {axis.ticks.map(({ value, xOffset, label }) => (
        <g key={value} transform={`translate(${String(xOffset)}, 0)`}>
          <line
            y1={0}
            y2={TICK_LENGTH}
            stroke="#D2D7D3"
            strokeWidth={0.5}
            shapeRendering={"crispEdges"}
          />

          <text
            key={value}
            style={{
              fontSize: "10px",
              textAnchor: "middle",
              transform: "translateY(20px)",
              fill: "black",
            }}
          >
            {label}
          </text>
        </g>
      ))}
    </>
  );
};
